import { useSearch } from "@tanstack/react-router";

/**
 * The URL state every list route carries.
 *
 * Kept here rather than on each route because every list has the same query
 * shape — the same reason `ListQuery` is one type in `@estelle/domain`.
 * Search, sort and the page cursor join it in their own tickets.
 */
export interface SectionSearch {
  /** The active filter, encoded. Absent when nothing is filtered. */
  filter?: string;
  /**
   * Free-text search over the section's searchable fields.
   *
   * `q` rather than `search`: it sits beside `filter` and `sort` in
   * every link, and the short name is the one people recognise from every
   * other search box on the web.
   */
  q?: string;
  /**
   * Active sort, as `column:direction` pairs joined by commas —
   * `placedAt:desc,total:asc`. Multi-column sort is ordinary in an ERP, and
   * the order of the pairs is the tie-break order.
   */
  sort?: string;
  /**
   * `grid`, `tile` or `warehouse`. Absent means this browser's preference, then the table
   * — see `resolveLayout`.
   */
  view?: string;
  /**
   * The record open in the drawer, by id. Absent when the list is showing.
   *
   * In the URL rather than in component state because a record with no
   * address cannot be refreshed, linked, or sent to anyone — "look at 1187"
   * stops being a link and becomes an instruction to go and find it. It sits
   * beside the list state rather than replacing it, so closing the drawer
   * returns to the same filtered, sorted list.
   */
  record?: string;
  /**
   * What a report's chart is set to — bucket, metric, dates, split — as one
   * encoded string (`modules/reports/orderChart.ts` owns the form). Beside the
   * filter so a chart, like a list, can be linked to and saved; only a report
   * section writes it, and it is carried through every list-state change so
   * filtering does not reset the chart.
   */
  chart?: string;
}

/**
 * Read search params off a URL someone may have typed, truncated or edited.
 *
 * Unknown parameters are dropped rather than carried: a link that accumulates
 * junk from an older version of the app is how a "shareable URL" stops being
 * one.
 */
export function validateSectionSearch(
  search: Record<string, unknown>,
): SectionSearch {
  const result: SectionSearch = {};
  if (typeof search.filter === "string" && search.filter !== "") {
    result.filter = search.filter;
  }
  // Same coercion as `record` below, for the same reason (#394, found while
  // fixing #387 there): TanStack Router's default search parser reads a bare
  // numeric-looking value — `?q=27` — as the number 27, not the string "27".
  // An order number, a SKU that happens to be all digits, or a phone number
  // typed into the free-text search box is exactly that shape, so a plain
  // `typeof === "string"` check here would silently drop the search term on
  // a fresh parse of the URL: a cold load, a shared link, or a reload.
  if (
    (typeof search.q === "string" || typeof search.q === "number") &&
    String(search.q) !== ""
  ) {
    result.q = String(search.q);
  }
  if (typeof search.sort === "string" && search.sort !== "") {
    result.sort = search.sort;
  }
  if (typeof search.view === "string" && search.view !== "") {
    result.view = search.view;
  }
  // TanStack Router's default search parser reads a bare numeric value —
  // `?record=27` — as the number 27, not the string "27". A contact id from
  // the live API is exactly that shape (Postgres `bigserial`, stringified),
  // so a plain `typeof === "string"` check here silently drops it on any
  // fresh parse of the URL: a cold load, a shared link, or a reload. The
  // fixture adapter's prefixed ids (`con_0001`) never triggered this, which
  // is why it went unnoticed until Contacts moved onto Postgres (#387).
  if (
    (typeof search.record === "string" || typeof search.record === "number") &&
    String(search.record) !== ""
  ) {
    result.record = String(search.record);
  }
  if (typeof search.chart === "string" && search.chart !== "") {
    result.chart = search.chart;
  }
  return result;
}

/**
 * The current route's search params, typed.
 *
 * `useSearch({ strict: false })` is `any` because it spans every route in the
 * tree. Re-validating on read costs nothing and turns that back into a type
 * the compiler can hold us to.
 */
export function useSectionSearch(): SectionSearch {
  const raw: unknown = useSearch({ strict: false });
  return validateSectionSearch(
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>)
      : {},
  );
}
